import { forwardRef, memo, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import Modal from 'react-bootstrap/Modal';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';

import { Form, FormContext, ResetButton, SubmitButton } from '../components/forms';
import { Button } from '../components/buttons';
import { setZIndexForModalForms } from '../../../lib/functions';
import DraggableModalDialog from './DraggableModalDialog';

/** Пропсы внутреннего FormContent (модальная форма). */
type FormContentProps = {
  show?: boolean;
  size?: 'sm' | 'lg' | 'xl';
  header?: ReactNode;
  cancelButtonTitle?: string;
  deleteButtonTitle?: string;
  submitButtonTitle?: string;
  showCancelButton?: boolean;
  showDeleteButton?: boolean;
  showSubmitButton?: boolean;
  disableCancelButton?: boolean;
  disableDeleteButton?: boolean;
  disableSubmitButton?: boolean;
  children?: ReactNode | ((formContext: any) => ReactNode);
  onClose: (formContext: any) => void;
  onDelete?: (formContext: any) => void;
  dialogClassName?: string;
  /** Перетаскивание окна за полоску-титулбар над header. При draggable заголовок
   *  и крестик закрытия переезжают в полоску (как в окне Windows), штатный header
   *  не рендерится. Не включать вместе с dialogClassName, меняющим позиционирование
   *  диалога (например, modal-dialog-canvas). */
  draggable?: boolean;
  /** Скроллится только Modal.Body (штатный .modal-dialog-scrollable react-bootstrap):
   *  header и footer остаются на месте — для длинного контента на планшетах/телефонах */
  scrollable?: boolean;
  showRequiredFieldsMessage?: boolean;
  ignoreDirtyOnSubmit?: boolean;
  addedButtons?: (dirty: boolean, values: any) => ReactNode;
  addedText?: (values: any) => ReactNode;
};

const FormContent = (props: FormContentProps) => {
  // FormContext из forms/utils.js: при allowJs:false useContext возвращает unknown,
  // приводим к ожидаемой форме (Provider подаёт Formik-контекст).
  const formContext = useContext(FormContext) as {
    values: any;
    dirty?: boolean;
    isSubmitting?: boolean;
    [key: string]: any;
  };
  const {
    show,
    size = 'lg', // 'sm' | 'lg' | 'xl'
    header,
    cancelButtonTitle = 'Отмена',
    deleteButtonTitle = 'Удалить',
    submitButtonTitle = 'Сохранить',
    showCancelButton = true,
    showDeleteButton = false,
    showSubmitButton = true,
    disableCancelButton = false,
    disableDeleteButton = false,
    disableSubmitButton = false,
    children,
    onClose,
    onDelete,
    dialogClassName,
    draggable = false,
    scrollable = false,
    showRequiredFieldsMessage = true,
    ignoreDirtyOnSubmit = false,
    addedButtons,
    addedText,
  } = props;

  useEffect(() => {
    setZIndexForModalForms();
  }, []);

  // Кастомные пропсы DraggableModalDialog (handleTitle/handleTitleId/onHandleClose)
  // отсутствуют в ModalProps: react-bootstrap протекает неизвестные пропсы в dialogAs.
  const draggableDialogProps = draggable ? {
    handleTitle: header,
    handleTitleId: 'form-modal',
    onHandleClose: () => onClose(formContext),
  } : {};

  return (
    <Modal
      show={ show }
      backdrop='static'
      size={ size }
      aria-labelledby='form-modal'
      onHide={ () => onClose(formContext) }
      dialogClassName={ dialogClassName }
      scrollable={ scrollable }
      dialogAs={ draggable ? DraggableModalDialog : undefined }
      { ...(draggableDialogProps as any) }
    >
      { !draggable && (
        <Modal.Header
          style={ { borderBottom: '1px solid #ececec', margin: '0 2px' } }
          closeButton
        >
          <Modal.Title id='form-modal'>
            { header }
          </Modal.Title>
        </Modal.Header>
      ) }

      <Modal.Body>
        { (typeof children === 'function') ? children(formContext) : children }

        { showRequiredFieldsMessage && (
          <Row>
            <Col lg={ 12 }>
              <small className='text-danger'>Поля, помеченные *, обязательны для заполнения</small>
            </Col>
          </Row>
        ) }
      </Modal.Body>

      <Modal.Footer style={ { borderTop: '1px solid #ececec', margin: '0 2px' } }>
        <ButtonToolbar className='float-start flex-grow-1'>
          { addedText ? addedText(formContext?.values) : null }
          { showDeleteButton && (
            <Button
              variant='danger'
              icon='trash'
              text={ deleteButtonTitle }
              disabled={ disableDeleteButton || formContext.isSubmitting }
              onClick={ () => onDelete(formContext) }
            />
          ) }
        </ButtonToolbar>

        <ButtonToolbar className='float-end'>
          { addedButtons ? addedButtons(formContext.dirty, formContext?.values) : null }
          { showCancelButton && (
            <ResetButton
              text={ cancelButtonTitle }
              disabled={ disableCancelButton || formContext.isSubmitting }
              variant='outline-primary'
            />
          ) }

          { showSubmitButton && (
            <SubmitButton
              text={ submitButtonTitle }
              disabled={ disableSubmitButton
                  || (!ignoreDirtyOnSubmit && !formContext.dirty)
                  || formContext.isSubmitting }
            />
          ) }
        </ButtonToolbar>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Пропсы модальной формы на базе Form.
 *
 * `onCancel` прокидывается в Form как onReset (кнопка «Отмена» = reset).
 * Внешний `ref` даёт доступ к formik-API формы (см. useImperativeHandle в Form).
 */
export interface FormModalProps {
  show?: boolean;
  loading?: boolean;
  size?: 'sm' | 'lg' | 'xl';
  header?: ReactNode;
  initialValues?: any;
  validationSchema?: any;
  cancelButtonTitle?: string;
  deleteButtonTitle?: string;
  submitButtonTitle?: string;
  showCancelButton?: boolean;
  showDeleteButton?: boolean;
  showSubmitButton?: boolean;
  disableCancelButton?: boolean;
  disableDeleteButton?: boolean;
  disableSubmitButton?: boolean;
  children?: ReactNode | ((formContext: any) => ReactNode);
  onCancel?: (...args: unknown[]) => unknown;
  onClose: (formContext: any) => void;
  onDelete?: (formContext: any) => void;
  onSubmit?: (values: any, formikHelpers: any) => void | Promise<any>;
  /**
   * Принимается для совместимости существующих вызовов; фактически всегда true —
   * захардкожено в Form внутри FormModal (передаваемое значение игнорируется).
   */
  enableReinitialize?: boolean;
  dialogClassName?: string;
  /** Перетаскивание за титулбар: header и крестик закрытия переезжают в полоску (см. DraggableModalDialog). */
  draggable?: boolean;
  /** Скроллится только Modal.Body (штатный .modal-dialog-scrollable react-bootstrap):
   *  header и footer остаются на месте — для длинного контента на планшетах/телефонах */
  scrollable?: boolean;
  showRequiredFieldsMessage?: boolean;
  ignoreDirtyOnSubmit?: boolean;
  addedButtons?: (dirty: boolean, values: any) => ReactNode;
  addedText?: (values: any) => ReactNode;
}

const FormModal = forwardRef<any, FormModalProps>((props, ref) => {
  const {
    initialValues,
    validationSchema,
    onCancel,
    onSubmit,
    ...rest
  } = props;

  if (!props.show) {
    return null;
  }

  return (
    <Form
      ref={ ref }
      initialValues={ initialValues }
      validationSchema={ validationSchema }
      onReset={ onCancel }
      onSubmit={ onSubmit }
      enableReinitialize

    >
      <FormContent { ...rest } />
    </Form>
  );
});

export default memo(FormModal);
